/* istanbul ignore file */

import { createTheme, PaletteColor, PaletteColorOptions } from '@mui/material';

// Titre, texte courant
export const roboto = 'Roboto, sans-serif';

// Surtitres, inter-titres, exergues
declare module '@mui/material/Button' {
    interface ButtonPropsVariantOverrides {
        neutral: true;
    }
}

// Style des box
declare module '@mui/material/Box' {
    interface BoxPropsVariantOverrides {
        greyBox: true;
    }
}

// Création de la palette de couleurs
declare module '@mui/material/styles/createPalette' {
    interface PaletteOptions {
        blue?: PaletteColorOptions;
        lightBlue?: PaletteColorOptions;
        skyBlue?: PaletteColorOptions;
        darkBlue?: PaletteColorOptions;
        green?: PaletteColorOptions;
        turquoise?: PaletteColorOptions;
        yellow?: PaletteColorOptions;
        brown?: PaletteColorOptions;
        lightGrey?: PaletteColorOptions;
        darkGrey?: PaletteColorOptions;
        red?: PaletteColorOptions;
    }

    interface Palette {
        blue: PaletteColor;
        lightBlue: PaletteColor;
        skyBlue: PaletteColor;
        darkBlue: PaletteColor;
        green: PaletteColor;
        turquoise: PaletteColor;
        yellow: PaletteColor;
        brown: PaletteColor;
        lightGrey: PaletteColor;
        darkGrey: PaletteColor;
        red: PaletteColor;
    }
}

/**
 * Ajout des couleurs dans un Theme pour l'ajouter dans le theme principal
 */
let data = createTheme({
    palette: {
        blue: {
            main: '#3e5ca7',
            light: '#e2e7f2',
        },
        lightBlue: {
            main: '#84c3df',
        },
        skyBlue: {
            main: '#3f83c5',
        },
        darkBlue: {
            main: '#23387b',
        },
        green: {
            main: '#95be72',
        },
        turquoise: {
            main: '#7ebaa7',
        },
        yellow: {
            main: '#eec109',
        },
        brown: {
            main: '#6f492b',
        },
        lightGrey: {
            main: '#e6e6e6',
            light: '#f2f5f6',
        },
        darkGrey: {
            main: '#808080',
        },
        red: {
            main: '#f44336',
        },
    },
});

/**
 * Theme avec en paramètre les couleurs
 * Et contenant le design SCSS de chaque component utilisé dans l'application
 */
const theme = createTheme(data, {
    data,
    components: {
        // Liens hypertexte
        MuiLink: {
            styleOverrides: {
                root: {
                    color: data.palette.blue.main,
                    fontFamily: roboto,
                },
            },
        },
        // Paper
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundColor: '#fff',
                    borderRadius: '3px',
                    fontFamily: roboto,
                },
            },
        },
        // Cards
        MuiCard: {
            styleOverrides: {
                root: {
                    backgroundColor: '#fff',
                    borderRadius: '3px',
                    fontFamily: roboto,
                },
            },
        },
        // Cards
        MuiCardHeader: {
            styleOverrides: {
                root: {
                    paddingBottom: '0px',
                },
            },
        },
        // Cards Media
        MuiCardMedia: {
            styleOverrides: {
                root: {
                    borderRadius: '10px',
                },
            },
        },
        // Inputs
        MuiInputBase: {
            styleOverrides: {
                input: {
                    fontFamily: roboto,
                },
                root: {
                    backgroundColor: 'white',
                    borderRadius: '0',
                },
            },
        },
        // Labels d'inputs
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    fontFamily: roboto,
                },
            },
        },
        // Typographies
        MuiTypography: {
            styleOverrides: {
                h4: {
                    color: data.palette.blue.main,
                    marginBottom: '20px',
                    fontFamily: roboto,
                    fontSize: 'xxx-large',
                    fontWeight: 'bold',
                    letterSpacing: '2px',
                    [data.breakpoints.down('md')]: {
                        fontSize: 'xx-large',
                    },
                },
                h5: {
                    color: data.palette.blue.main,
                    fontFamily: roboto,
                    fontWeight: 'bold',
                    marginBottom: '10px',
                    letterSpacing: '2px',
                },
                h6: {
                    color: data.palette.blue.main,
                    fontFamily: roboto,
                    fontSize: 'small',
                },
                body1: {
                    fontFamily: roboto,
                },
                body2: {
                    fontFamily: roboto,
                },
                subtitle1: {
                    fontFamily: roboto,
                    fontWeight: 'bold',
                    letterSpacing: '2px',
                },
                subtitle2: {
                    fontFamily: roboto,
                    fontSize: 'small',
                    color: data.palette.darkGrey.main,
                },
            },
        },
        // Boutons
        MuiButton: {
            variants: [
                {
                    props: { variant: 'contained' },
                    style: {
                        textAlign: 'center',
                        color: '#fff',
                        backgroundColor: data.palette.blue.main,
                        '&:hover': {
                            color: '#fff',
                            backgroundColor: data.palette.skyBlue.main,
                        },
                        fontFamily: roboto,
                    },
                },
                {
                    props: { variant: 'outlined' },
                    style: {
                        textAlign: 'center',
                        color: '#fff',
                        borderColor: '#fff',
                        textTransform: 'none',
                        '&:hover': {
                            color: data.palette.blue.main,
                            backgroundColor: '#fff',
                        },
                        fontFamily: roboto,
                    },
                },
                {
                    props: { variant: 'text' },
                    style: {
                        textAlign: 'center',
                        color: data.palette.blue.main,
                        backgroundColor: 'none',
                        textTransform: 'none',
                        '&:hover': {
                            color: data.palette.blue.main,
                            backgroundColor: data.palette.lightGrey.main,
                        },
                        fontFamily: roboto,
                    },
                },
                {
                    props: { variant: 'neutral' },
                    style: {
                        textAlign: 'center',
                        color: '#fff',
                        backgroundColor: data.palette.darkGrey.main,
                        '&:hover': {
                            color: '#fff',
                            backgroundColor: data.palette.skyBlue.main,
                        },
                        fontFamily: roboto,
                    },
                },
            ],
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    marginBottom: '20px',
                    backgroundColor: '#fff',
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                root: {
                    marginBottom: '20px',
                },
            },
        },
    },
});

export default theme;
